import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { rulesAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { btnDanger, btnGhost, btnPrimary, EmptyState, Field, inputCls, Modal, panelCls, StatusBadge } from '../../components/ui';
import { PRODUCT_LABEL, PRODUCTS, ProductBadge, ProductFilter } from '../../components/products';

const CURRENCIES = ['points', 'xp', 'cash', 'credit'];

const BLANK = {
  name: '', description: '', trigger: '', active: true, category: '',
  awards: [{ currency: 'points', amount: 10 }],
  caps: { per_day: '', total: '' },
  cooldown_seconds: '', once_per_subject: false,
  award_status: 'approved',
  audience: { mode: 'all', mids: [] },
  schedule: { start: '', end: '' },
};

function RuleModal({ rule, onClose, onSaved }) {
  const [form, setForm] = useState(rule ? {
    ...BLANK,
    ...rule,
    caps: { per_day: rule.caps?.per_day ?? '', total: rule.caps?.total ?? '' },
    cooldown_seconds: rule.cooldown_seconds || '',
    audience: { mode: rule.audience?.mode || 'all', mids: rule.audience?.mids || [] },
    schedule: { start: rule.schedule?.start || '', end: rule.schedule?.end || '' },
  } : BLANK);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setAward = (i, patch) =>
    set({ awards: form.awards.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        awards: form.awards.map((a) => ({ currency: a.currency, amount: Number(a.amount), ...(a.per ? { per: a.per } : {}) })),
        caps: {
          ...(form.caps.per_day !== '' ? { per_day: Number(form.caps.per_day) } : {}),
          ...(form.caps.total !== '' ? { total: Number(form.caps.total) } : {}),
        },
        cooldown_seconds: form.cooldown_seconds === '' ? 0 : Number(form.cooldown_seconds),
      };
      if (rule) await rulesAPI.update(rule.id, payload);
      else await rulesAPI.create(payload);
      toast.success(t('Saved'));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={rule ? t('Edit rule') : t('New rule')} onClose={onClose} wide>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('Name')}>
          <input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label={t('Trigger (event type)')} hint={t('e.g. login, lesson_completed, referral_paid')}>
          <input className={inputCls} value={form.trigger} onChange={(e) => set({ trigger: e.target.value })} />
        </Field>
        <Field label={t('Product')} hint={t('Empty = detected from the trigger')}>
          <select className={inputCls} value={form.category || ''} onChange={(e) => set({ category: e.target.value })}>
            <option value="">{t('Automatic')}{rule?.product ? ` (${t(PRODUCT_LABEL[rule.product])})` : ''}</option>
            {PRODUCTS.map((p) => <option key={p} value={p}>{t(PRODUCT_LABEL[p])}</option>)}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field label={t('Description')}>
            <input className={inputCls} value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <span className="block text-sm text-mms-muted mb-1.5">{t('Awards')}</span>
          <div className="space-y-2">
            {form.awards.map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  className={`${inputCls} !w-36`}
                  value={a.currency}
                  onChange={(e) => setAward(i, { currency: e.target.value })}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{t(c)}</option>
                  ))}
                </select>
                <input
                  type="number"
                  step="any"
                  className={`${inputCls} !w-32`}
                  value={a.amount}
                  onChange={(e) => setAward(i, { amount: e.target.value })}
                />
                {form.awards.length > 1 && (
                  <button
                    onClick={() => set({ awards: form.awards.filter((_, j) => j !== i) })}
                    className="text-mms-muted hover:text-[var(--mms-danger)]"
                    aria-label={t('Remove')}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            ))}
            <button
              onClick={() => set({ awards: [...form.awards, { currency: 'xp', amount: 10 }] })}
              className={btnGhost}
            >
              <Plus size={14} /> {t('Add award')}
            </button>
          </div>
        </div>

        <Field label={t('Cap per day')} hint={t('Empty = unlimited')}>
          <input type="number" min="1" className={inputCls} value={form.caps.per_day}
            onChange={(e) => set({ caps: { ...form.caps, per_day: e.target.value } })} />
        </Field>
        <Field label={t('Cap total (per member)')} hint={t('Empty = unlimited')}>
          <input type="number" min="1" className={inputCls} value={form.caps.total}
            onChange={(e) => set({ caps: { ...form.caps, total: e.target.value } })} />
        </Field>
        <Field label={t('Cooldown (seconds)')} hint={t('Empty = none')}>
          <input type="number" min="0" className={inputCls} value={form.cooldown_seconds}
            onChange={(e) => set({ cooldown_seconds: e.target.value })} />
        </Field>
        <Field label={t('Award status')} hint={t('Pending = staff approve before it counts')}>
          <select className={inputCls} value={form.award_status}
            onChange={(e) => set({ award_status: e.target.value })}>
            <option value="approved">{t('approved')}</option>
            <option value="pending">{t('pending')}</option>
          </select>
        </Field>

        <Field label={t('Schedule start')} hint={t('ISO date-time, empty = always')}>
          <input className={inputCls} placeholder="2026-08-01T00:00:00+00:00" value={form.schedule.start}
            onChange={(e) => set({ schedule: { ...form.schedule, start: e.target.value } })} />
        </Field>
        <Field label={t('Schedule end')}>
          <input className={inputCls} placeholder="" value={form.schedule.end}
            onChange={(e) => set({ schedule: { ...form.schedule, end: e.target.value } })} />
        </Field>

        <Field label={t('Audience')}>
          <select className={inputCls} value={form.audience.mode}
            onChange={(e) => set({ audience: { ...form.audience, mode: e.target.value } })}>
            <option value="all">{t('All members')}</option>
            <option value="mids">{t('Specific members (mids)')}</option>
          </select>
        </Field>
        {form.audience.mode === 'mids' && (
          <Field label={t('Member ids (comma-separated)')}>
            <input className={inputCls} value={form.audience.mids.join(', ')}
              onChange={(e) => set({ audience: { ...form.audience, mids: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })} />
          </Field>
        )}

        <div className="flex items-center gap-4 sm:col-span-2">
          <label className="flex items-center gap-2 text-sm text-mms-muted">
            <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />
            {t('Active')}
          </label>
          <label className="flex items-center gap-2 text-sm text-mms-muted">
            <input type="checkbox" checked={form.once_per_subject} onChange={(e) => set({ once_per_subject: e.target.checked })} />
            {t('Once per subject (e.g. one award per post/lesson, ever)')}
          </label>
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button onClick={onClose} className={btnGhost}>{t('Cancel')}</button>
        <button onClick={save} disabled={busy} className={btnPrimary}>
          {busy ? t('Saving…') : t('Save')}
        </button>
      </div>
    </Modal>
  );
}

export default function RulesPage() {
  const [rules, setRules] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const [product, setProduct] = useState('');

  const load = useCallback(() => {
    rulesAPI.list().then(({ data }) => setRules(data.rules)).catch(() => toast.error(t('Could not load rules')));
  }, []);
  useEffect(load, [load]);

  const remove = async (rule) => {
    if (!window.confirm(t('Delete this rule? Ledger history keeps its name.'))) return;
    try {
      await rulesAPI.remove(rule.id);
      toast.success(t('Deleted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-mms-text">{t('Earning rules')}</h1>
          <p className="mt-1 text-[12.5px] text-mms-muted">{t('An event triggers an award, subject to caps, cooldown and audience.')}</p>
        </div>
        <button onClick={() => setEditing(null)} className={btnPrimary}>
          <Plus size={15} /> {t('New rule')}
        </button>
      </div>

      {rules && rules.length > 0 && (
        <div className="mb-3">
          <ProductFilter value={product} onChange={setProduct}
            counts={rules.reduce((acc, r) => ({ ...acc, [r.product]: (acc[r.product] || 0) + 1, all: (acc.all || 0) + 1 }), {})} />
        </div>
      )}

      {rules === null ? (
        <p className="text-sm text-mms-muted">{t('Loading…')}</p>
      ) : rules.length === 0 ? (
        <EmptyState>{t('No rules yet — create the first one.')}</EmptyState>
      ) : (
        <div className={`${panelCls} overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-mms-border text-left text-xs text-mms-faint">
                <th className="px-4 py-2.5 font-medium">{t('Name')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Product')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Trigger')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Awards')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Gates')}</th>
                <th className="px-4 py-2.5 font-medium">{t('Status')}</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rules.filter((r) => !product || r.product === product).map((r) => (
                <tr key={r.id} className="border-b border-mms-border/60 last:border-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{r.name}</div>
                    {r.description ? <div className="text-xs text-mms-faint">{r.description}</div> : null}
                  </td>
                  <td className="px-4 py-2.5"><ProductBadge product={r.product} /></td>
                  <td className="px-4 py-2.5"><code className="text-xs text-mms-muted">{r.trigger}</code></td>
                  <td className="px-4 py-2.5">
                    {(r.awards || []).map((a) => (
                      <span key={a.currency} className="mr-2 mms-num">{a.amount} {t(a.currency)}{a.per ? ` / ${t('per')} ${a.per}` : ''}</span>
                    ))}
                    {r.award_status === 'pending' && <StatusBadge status="pending" />}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-mms-muted">
                    {[
                      r.caps?.per_day ? `${r.caps.per_day}/${t('day')}` : null,
                      r.caps?.total ? `${t('max')} ${r.caps.total}` : null,
                      r.cooldown_seconds ? `${r.cooldown_seconds}s` : null,
                      r.once_per_subject ? t('per subject') : null,
                      r.audience?.mode === 'mids' ? `${r.audience.mids.length} ${t('members')}` : null,
                    ].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={r.active ? 'active' : 'inactive'} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => setEditing(r)} className={`${btnGhost} mr-2`}>{t('Edit')}</button>
                    <button onClick={() => remove(r)} className={btnDanger}>{t('Delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <RuleModal
          rule={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); load(); }}
        />
      )}
    </div>
  );
}
